import { Grid } from '@mui/material';
import type { ODSTagStaticType } from '@telekom-ods/react-ui-kit';
import { useIntl } from 'react-intl';

import InfoRow from './InfoRow';

import type { ClusterDetailsResponse } from '@/types/caasTypes';

interface ClusterInfoProps {
  details: ClusterDetailsResponse | null;
  owner: string | Record<string, unknown>;
  collapsible?: boolean;
}

interface VersionTarget {
  label: string;
  value: string;
  tagType: ODSTagStaticType;
  selectable: boolean;
}

const PLACEHOLDER = '—';

const buildTargets = (details: ClusterDetailsResponse | null): VersionTarget[] => {
  if (!details) return [];
  const current = details.cluster_version;
  const candidates: {
    label: string;
    value?: string;
    tagType: ODSTagStaticType;
    selectable: boolean;
  }[] = [
    {
      label: 'latest patch',
      value: details.latest_patch_version,
      tagType: 'success',
      selectable: true,
    },
    {
      label: 'latest minor',
      value: details.latest_minor_version,
      tagType: 'warning',
      selectable: true,
    },
    {
      label: 'next latest',
      value: details.next_latest_version,
      tagType: 'subtle',
      selectable: true,
    },
    {
      label: 'latest major',
      value: details.latest_major_version,
      tagType: 'error',
      selectable: false,
    },
  ];
  const seen = new Set<string>();
  const targets: VersionTarget[] = [];
  for (const c of candidates) {
    if (!c.value || c.value === current || seen.has(c.value)) continue;
    seen.add(c.value);
    targets.push({ label: c.label, value: c.value, tagType: c.tagType, selectable: c.selectable });
  }
  return targets;
};

const ClusterInfo = ({
  details,
  owner,
  collapsible = false,
}: ClusterInfoProps): React.JSX.Element => {
  const intl = useIntl();
  const ownerDisplay = typeof owner === 'string' && owner ? owner : 'N/A';

  const currentVersion = details?.cluster_version ?? PLACEHOLDER;
  const targets = buildTargets(details);
  const selectableTargets = targets.filter(t => t.selectable);
  const availableCount = selectableTargets.length;

  const detailRows = (
    <Grid
      container
      direction='column'
      gap={1.5}
    >
      <InfoRow
        label={intl.formatMessage({ id: 'currentVersion' })}
        value={currentVersion}
        tagType='strong'
      />
      {targets.map(t => (
        <InfoRow
          key={t.label}
          label={t.label.replace(/^\w/, ch => ch.toUpperCase())}
          value={t.value}
          tagType={t.tagType}
        />
      ))}
      <InfoRow
        label={intl.formatMessage({ id: 'parent' })}
        value={details?.parent ?? PLACEHOLDER}
      />
      <InfoRow
        label={intl.formatMessage({ id: 'owner' })}
        value={ownerDisplay}
      />
    </Grid>
  );

  if (!collapsible) return detailRows;

  const summary =
    availableCount > 0
      ? `${currentVersion} · ${String(availableCount)} version${availableCount > 1 ? 's' : ''} available`
      : `${currentVersion} · latest`;

  return (
    <details className='upgrade-acc'>
      <summary className='upgrade-acc-sum'>
        <span
          className='upgrade-acc-chev'
          aria-hidden='true'
        >
          ›
        </span>
        <span className='upgrade-acc-title'>Cluster info</span>
        <span className='upgrade-acc-status'>
          <span className='upgrade-acc-sub'>{summary}</span>
        </span>
      </summary>
      <div className='upgrade-acc-body'>{detailRows}</div>
    </details>
  );
};

export default ClusterInfo;
